import type React from 'react';
import { Fragment } from 'react';

import { cn } from '#src/app/shared/components/adminShadcn';

import type { AudienceBuilderAllowedFilter, BaseFilterType } from '../shared/types';

type FilterVariant = 'inclusion' | 'exclusion';

const SECTION_STYLES: Record<FilterVariant, { label: string; group: string; filter: string; or: string; and: string }> =
	{
		inclusion: {
			label: 'admin:text-green-700',
			group: 'admin:bg-green-100',
			filter: 'admin:text-green-800',
			or: 'admin:border admin:border-green-300 admin:bg-white admin:text-green-700',
			and: 'admin:bg-green-600 admin:text-white',
		},
		exclusion: {
			label: 'admin:text-amber-700',
			group: 'admin:bg-amber-100',
			filter: 'admin:text-amber-800',
			or: 'admin:border admin:border-amber-300 admin:bg-white admin:text-amber-700',
			and: 'admin:bg-amber-600 admin:text-white',
		},
	};

/** Turn the raw filter groups into non-empty groups of human-readable labels. */
function toLabelGroups(
	groups: BaseFilterType | undefined,
	allowedFilters: readonly AudienceBuilderAllowedFilter[],
): string[][] {
	return (Array.isArray(groups) ? groups : [])
		.map((group) =>
			group
				.filter((filter) => filter.type || filter.value)
				.map((filter) => {
					const name = allowedFilters.find(({ type }) => type === filter.type)?.name ?? filter.type;
					return filter.value ? `${name}: ${filter.value}` : name;
				}),
		)
		.filter((group) => group.length > 0);
}

const FilterSection: React.FC<{ label: string; variant: FilterVariant; groups: string[][] }> = ({
	label,
	variant,
	groups,
}) => {
	const styles = SECTION_STYLES[variant];
	return (
		<div className="admin:flex admin:flex-wrap admin:items-center admin:gap-2 admin:text-sm">
			<span className={cn('admin:font-semibold', styles.label)}>{label}</span>
			{groups.map((group, groupIndex) => (
				// eslint-disable-next-line react/no-array-index-key
				<Fragment key={groupIndex}>
					{groupIndex > 0 && (
						<span
							className={cn('admin:rounded-full admin:px-2 admin:py-0.5 admin:text-xs admin:font-bold', styles.and)}
						>
							AND
						</span>
					)}
					<div
						className={cn(
							'admin:flex admin:flex-wrap admin:items-center admin:gap-1.5 admin:rounded-md admin:px-2 admin:py-1',
							styles.group,
						)}
					>
						{group.map((filterLabel, filterIndex) => (
							// eslint-disable-next-line react/no-array-index-key
							<Fragment key={filterIndex}>
								{filterIndex > 0 && (
									<span
										className={cn(
											'admin:rounded-full admin:px-2 admin:py-0.5 admin:text-xs admin:font-semibold',
											styles.or,
										)}
									>
										OR
									</span>
								)}
								<span className={cn('admin:text-xs', styles.filter)}>{filterLabel}</span>
							</Fragment>
						))}
					</div>
				</Fragment>
			))}
		</div>
	);
};

/**
 * Read-only summary of an audience builder's filters, rendered as chips: each
 * AND clause is a colour-coded group box (green inclusions / amber exclusions)
 * with `OR` chips between alternatives, and `AND` chips between the groups. The
 * row wraps when long. Returns `null` when there are no filters.
 */
export const AudienceFiltersPreview: React.FC<{
	inclusions?: BaseFilterType;
	exclusions?: BaseFilterType;
	allowedFilters: readonly AudienceBuilderAllowedFilter[];
}> = ({ inclusions, exclusions, allowedFilters }) => {
	const inclusionGroups = toLabelGroups(inclusions, allowedFilters);
	const exclusionGroups = toLabelGroups(exclusions, allowedFilters);

	if (!inclusionGroups.length && !exclusionGroups.length) {
		return null;
	}

	return (
		<div className="admin:flex admin:flex-col admin:gap-2">
			{inclusionGroups.length > 0 && <FilterSection label="Inclusions:" variant="inclusion" groups={inclusionGroups} />}
			{exclusionGroups.length > 0 && <FilterSection label="Exclusions:" variant="exclusion" groups={exclusionGroups} />}
		</div>
	);
};
